/*
 * User configuration, permissions and settings
 *
 * User.js
 *
 * (c) 2019 FSZ GmbH
 * All rights reserved.
 */

'use strict';

// external imports
import _ from 'lodash';
import {enumerate} from 'ts-transformer-enumerate';

import {MenuItem} from 'primereact/menuitem';

// shared imports
import messageCatalog from '~/shared/src/utilities/messageCatalog';
import metaModel from '~/shared/src/utilities/metaModel';
import ItemType from '~/shared/src/mm/ItemType';
import PropertySet from '~/shared/src/mm/PropertySet';
import ReferenceSet from '~/shared/src/mm/ReferenceSet';
import ReferencePropertySet from '~/shared/src/mm/ReferencePropertySet';
import Item, {ItemJsonAttributes} from '~/shared/src/ci/Item';
import Application, {ApplicationData} from '~/shared/src/model/Application';
import {convertValueToString} from '~/shared/src/utilities/valueConverters';
import ciHelper from '~/shared/src/utilities/dataUtils';
import {getIconCssClassName} from '~/shared/src/utilities/getCssClassName';
import getHierarchy from '~/shared/src/utilities/hierarchyBuilder';
import LocalizableError from '~/shared/src/base/LocalizableError';
import {dataExtraction} from '~/shared/src/utilities/dataExtraction';

// frontend imports
import {PanelPropsWithPanelType} from '~/frontend/src/actions/panelManager/openPanel';
///////////////////////////

//////////

// backend imports
///////////////////////////
/////////////////////////////////////////////
////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////
////////
//////////
/////////
////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////
//////////


export type Permission = 0 | 1 | 2 | 3 | 4;
export type YnPermissionName = 'none' | 'read' | 'update' | 'insert' | 'delete';
export const ynPermissionNames = enumerate<YnPermissionName>();

export type GenericPermission = 0 | 1 | 2 | 3;
export type GenericPermissionName = 'none' | 'read' | 'nonBlocked' | 'full';
export const genericPermissionNames = enumerate<GenericPermissionName>();

interface PermissionConfig {
    [ index : string ] : {
        permission : Permission;
        genericPermission : GenericPermission;
    };
}

type SchemaPermissions = {
    [schema in Schema]? : {
        itemType : PermissionConfig;
        propertySet : PermissionConfig;
        referenceSet : PermissionConfig;
        referencePropertySet : PermissionConfig;
    };
};
type ObjectPermissions = { object? : { [ objectId : number ] : { permission : Permission } } };
type Permissions = SchemaPermissions & ObjectPermissions;

type RpcPermissions = {
    [ module : string ] : {
        [ command : string ] : true
    }
};

type PanelConfig = {
    meta : {
        isMobileOptimized : boolean;
        order? : number;
    };
    panelConfig : PanelPropsWithPanelType;
};
type PanelConfigs = {
    [ id : number ] : PanelConfig;
};

export type UserData = {
    userId : string;
    item : ItemJsonAttributes;
    groupMemberships : string[];
    mainMenu? : MenuItem[];
    panelConfigs : PanelConfigs;
    itemTypeToPanelConfigMapping : ItemTypeToPanelConfigMapping;
    permissions : Permissions;
    rpcPermissions : RpcPermissions;
    applications : ApplicationData[];
}
export type UserDataFromJSON = {
    userId : string;
    item : ItemJsonAttributes;
    groupMemberships? : string[];
    mainMenu? : MenuItem[];
    panelConfigs? : PanelConfigs;
    itemTypeToPanelConfigMapping? : ItemTypeToPanelConfigMapping;
    permissions? : Permissions;
    rpcPermissions? : RpcPermissions;
    applications? : ApplicationData[];
}

export type View = {
    key : string;
    label : string;
    new? : boolean;
    panelProps : PanelProps;
}

export type ViewToOpenByDefault = 'nongeneric' | 'generic';

type WaitForReady = Promise<boolean> & { resolve? : Function, reject? : Function };

type PermissionNameToNumberMapping = {
    [name in YnPermissionName] : Permission;
}

type PermissionNumberToNameMapping = {
    [name in Permission] : YnPermissionName;
}

type GenericPermissionNameToNumberMapping = {
    [name in GenericPermissionName] : GenericPermission;
}

type GenericPermissionNumberToNameMapping = {
    [name in GenericPermission] : GenericPermissionName;
}

type ItemTypeToPanelConfigMapping = {
    [ schema : string ] : {
        [ itemTypId : number ] : number[];
    }
}

/**
 *
 */
export class User {

    static permissionNameToNumberMapping : PermissionNameToNumberMapping = {
        none  : 0,
        read  : 1,
        update: 2,
        insert: 3,
        delete: 4
    };

    static permissionNumberToNameMapping : PermissionNumberToNameMapping = {
        0: 'none',
        1: 'read',
        2: 'update',
        3: 'insert',
        4: 'delete'
    };

    static genericPermissionNameToNumberMapping : GenericPermissionNameToNumberMapping = {
        none      : 0,
        read      : 1,
        nonBlocked: 2,
        full      : 3
    };

    static genericPermissionNumberToNameMapping : GenericPermissionNumberToNameMapping = {
        0: 'none',
        1: 'read',
        2: 'nonBlocked',
        3: 'full'
    };


    /**
     * Flag signaling the state of this (false on async (DB) activity)
     * @type {Boolean}
     * @default false
     */
    isReady : boolean = false;

    readonly userId : string = '';

    groupMemberships : string[] = [];
    panelConfigs : PanelConfigs = {};
    itemTypeToPanelConfigMapping : ItemTypeToPanelConfigMapping = {};
    permissions : Permissions = {};
    rpcPermissions : RpcPermissions = {};

    applications : Application[] = [];

    mainMenu : MenuItem[] = [];

    waitForReady : WaitForReady | null = null;

    baseErrorNumber : number = 1000;

    viewConfigSeparator : string = '::';

    _userItemId : number = 0;

    /**
     * @param {UserData | string} userConfig
     * @param {boolean} loadUserDataFromDB (optional, default: true)
     */
    constructor( userConfig : UserDataFromJSON | string, loadUserDataFromDB : boolean = true ) {
        if ( typeof userConfig === 'object' ) {
            this.validateUserId( userConfig.userId );
            this.userId = userConfig.userId;
            this.fromJSON( userConfig );
        }
        else {
            this.validateUserId( userConfig );
            this.userId = userConfig;

            this.createWaitForReady();

            this.loadFromDB( loadUserDataFromDB ).catch( ( e ) => {
                error( e instanceof Error ? e.message : e );
            } );
        }
    }

    static permissionNameToPermission( maybePermission : string ) : Permission {
        const permissionName = ynPermissionNames.find( ( validPermissionName ) => validPermissionName === maybePermission );
        if ( permissionName ) {
            // 'permissionName' comes from the list of 'ynPermissionNames' so the compiler is happy.
            return User.permissionNameToNumberMapping[ permissionName ];
        }
        throw ( new Error( `invalid permission '${maybePermission}'` ) );
    }

    static genericPermissionNameToGenericPermission( maybeGenericPermission : string ) : GenericPermission {
        const genericPermissionName = genericPermissionNames.find( ( validGenericPermissionName ) => validGenericPermissionName === maybeGenericPermission );
        if ( genericPermissionName ) {
            // 'genericPermissionName' comes from the list of 'genericPermissionNames' so the compiler is happy.
            return User.genericPermissionNameToNumberMapping[ genericPermissionName ];
        }
        throw ( new Error( `invalid generic permission '${maybeGenericPermission}'` ) );
    }

    /**
     * Return user data JSON Object
     */
    toJSON() : UserData {
        return {
            userId                      : this.userId,
            item                        : this.item?.toJSON() ?? {} as ItemJsonAttributes,
            groupMemberships            : this.groupMemberships,
            panelConfigs                : this.panelConfigs,
            itemTypeToPanelConfigMapping: this.itemTypeToPanelConfigMapping,
            permissions                 : this.permissions,
            rpcPermissions              : this.rpcPermissions,
            applications                : this.applications.map( app => app.toJSON() ),
            mainMenu                    : this.mainMenu
        };
    }

    /**
     * Creates and returns a promise which will be resolved if the instance is ready.
     *
     * @return {Promise<boolean>}
     */
    async ifReady() : Promise<boolean> {
        if ( this.isReady ) {
            return true;
        }

        if ( this.waitForReady ) {
            return this.waitForReady;
        }

        return this.isReady;
    }

    /**
     * Set ready state by resolving the waitForReady promise
     */
    setReady() {
        this.isReady = true;

        if ( this.waitForReady ) {
            this.waitForReady.resolve?.( this.isReady );
            this.waitForReady = null;
        }
    }

    /**
     * Set ready state by rejecting the waitForReady promise
     */
    setFailed( e : any ) {
        this.isReady = false;

        if ( this.waitForReady ) {
            this.waitForReady.reject?.( e );
            this.waitForReady = null;
        }
    }


    /*** misc *****************************************************************************************************************************/

    /**
     * Clone user
     */
    clone() : User {
        return new User( this.toJSON() );
    }

    get item() : Item | undefined {
        return global.data[ this.itemContext ]?.items[ this._userItemId ];
    }

    get application() : Application | undefined {
        const applicationName = this.applicationName;

        return this.applications.find( app => app.key === applicationName );
    }

    /**
     * Gets the users name from item
     *
     * @return {string}
     */
    get name() : string {
        return this.item?.name ?? '';
    }

    /**
     * Gets the users application from item
     *
     * @return {string}
     */
    get applicationName() : string {
        const propertyType = metaModel[ 'CFG' ].getPropertyType( 'Application' ),
              property     = propertyType && this.item ? this.item.properties.get( propertyType.id ) : undefined;

        // fall back to configured application
        let retVal = config.application.name;

        if ( property?.value && this.applications.map( app => app.key ).includes( String( property.value ) ) ) {
            retVal = String( property.value );
        }

        return retVal;
    }

    /**
     * Sets the users application to item
     *
     * @param {string} application
     *
     * @return {void}
     */
    set applicationName( application : string ) {
        if ( this.applications.map( app => app.key ).includes( application ) ) {
            const propertyType = metaModel[ 'CFG' ].getPropertyType( 'Application' )

            if ( propertyType && this.item ) {
                let property = this.item.properties.get( propertyType.id );

                if ( !property ) {
                    property = this.item.addProperty( propertyType.id );
                }

                property.value = application;
            }
        }
        else {
            throw new Error( `error switching user application '${application}': name not valid` );
        }
    }

    /**
     * Gets the users language from item
     *
     * @return {string}
     */
    get language() : string {
        const propertyType = metaModel[ 'CFG' ].getPropertyType( 'Language' ),
              property     = propertyType && this.item ? this.item.properties.get( propertyType.id ) : undefined;

        let retVal : string = '';

        if ( property?.value ) {
            retVal = String( property.value );
        }
        else {
            retVal = ( metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( 'User', 'Language' )?.valueDefault as string | undefined ) ?? messageCatalog.language;
        }

        return retVal;
    }

    /**
     * Sets the users language to item
     *
     * @param {string} language
     *
     * @return {void}
     */
    set language( language : string ) {
        const propertyType = metaModel[ 'CFG' ].getPropertyType( 'Language' )

        if ( propertyType && this.item ) {
            let property = this.item.properties.get( propertyType.id );

            if ( !property ) {
                property = this.item.addProperty( propertyType.id );
            }

            property.value = language;
        }
    }

    /**
     * Gets the users viewToOpenByDefault from item
     *
     * @return {ViewToOpenByDefault}
     */
    get viewToOpenByDefault() : ViewToOpenByDefault {
        const propertyType = metaModel[ 'CFG' ].getPropertyType( 'ViewToOpenByDefault' ),
              property     = propertyType && this.item ? this.item.properties.get( propertyType.id ) : undefined;

        let retVal : ViewToOpenByDefault = 'nongeneric';

        if ( property?.value ) {
            retVal = String( property.value ) as ViewToOpenByDefault;
        }
        else {
            const valueDefault = metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( 'User', 'ViewToOpenByDefault' )?.valueDefault as ViewToOpenByDefault | undefined;

            if ( valueDefault ) {
                retVal = valueDefault;
            }
        }

        return retVal;
    }

    /**
     * Sets the users viewToOpenByDefault to item
     *
     * @param {ViewToOpenByDefault} viewToOpenByDefault
     *
     * @return {void}
     */
    set viewToOpenByDefault( viewToOpenByDefault : ViewToOpenByDefault ) {
        const propertyType = metaModel[ 'CFG' ].getPropertyType( 'ViewToOpenByDefault' )

        if ( propertyType && this.item ) {
            let property = this.item.properties.get( propertyType.id );

            if ( !property ) {
                property = this.item.addProperty( propertyType.id );
            }

            property.value = viewToOpenByDefault;
        }
    }

    /**
     * Gets the users email address from item
     *
     * @return {string}
     */
    get email() : string {
        const propertyType = metaModel[ 'CFG' ].getPropertyType( 'Email' ),
              property     = propertyType && this.item ? this.item.properties.get( propertyType.id ) : undefined;

        let retVal : string = '';

        if ( property?.value ) {
            retVal = String( property.value );
        }

        return retVal;
    }

    /**
     * Sets the users email address to item
     *
     * @param {string} email
     *
     * @return {void}
     */
    set email( email : string ) {
        const propertyType = metaModel[ 'CFG' ].getPropertyType( 'Email' )

        if ( propertyType && this.item ) {
            let property = this.item.properties.get( propertyType.id );

            if ( !property ) {
                property = this.item.addProperty( propertyType.id );
            }

            property.value = email;
        }
    }

    /**
     * Gets the users first name from item
     *
     * @return {string}
     */
    get firstName() : string {
        const propertyType = metaModel[ 'CFG' ].getPropertyType( 'Firstname' ),
              property     = propertyType && this.item ? this.item.properties.get( propertyType.id ) : undefined;

        let retVal : string = '';

        if ( property?.value ) {
            retVal = String( property.value );
        }

        return retVal;
    }

    /**
     * Sets the users first name to item
     *
     * @param {string} firstName
     *
     * @return {void}
     */
    set firstName( firstName : string ) {
        const propertyType = metaModel[ 'CFG' ].getPropertyType( 'Firstname' )

        if ( propertyType && this.item ) {
            let property = this.item.properties.get( propertyType.id );

            if ( !property ) {
                property = this.item.addProperty( propertyType.id );
            }

            property.value = firstName;
        }
    }

    /**
     * Gets the users last name from item
     *
     * @return {string}
     */
    get lastName() : string {
        const propertyType = metaModel[ 'CFG' ].getPropertyType( 'Lastname' ),
              property     = propertyType && this.item ? this.item.properties.get( propertyType.id ) : undefined;

        let retVal : string = '';

        if ( property?.value ) {
            retVal = String( property.value );
        }

        return retVal;
    }

    /**
     * Sets the users last name to item
     *
     * @param {string} lastName
     *
     * @return {void}
     */
    set lastName( lastName : string ) {
        const propertyType = metaModel[ 'CFG' ].getPropertyType( 'Lastname' )

        if ( propertyType && this.item ) {
            let property = this.item.properties.get( propertyType.id );

            if ( !property ) {
                property = this.item.addProperty( propertyType.id );
            }

            property.value = lastName;
        }
    }

    /**
     * Gets the users last name from item
     *
     * @return {string}
     */
    get tabBarPosition() : string {
        const propertyType = metaModel[ 'CFG' ].getPropertyType( 'TabBarPosition' ),
              property     = propertyType && this.item ? this.item.properties.get( propertyType.id ) : undefined;

        let retVal : string = '';

        if ( property?.value ) {
            retVal = String( property.value );
        }
        else {
            const valueDefault = metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( 'User', 'TabBarPosition' )?.valueDefault as string | undefined;

            if ( valueDefault ) {
                retVal = valueDefault;
            }
        }

        return retVal;
    }

    /**
     * Sets the users last name to item
     *
     * @param {string} lastName
     *
     * @return {void}
     */
    set tabBarPosition( tabBarLayout : string ) {
        const propertyType = metaModel[ 'CFG' ].getPropertyType( 'TabBarPosition' )

        if ( propertyType && this.item ) {
            let property = this.item.properties.get( propertyType.id );

            if ( !property ) {
                property = this.item.addProperty( propertyType.id );
            }

            property.value = tabBarLayout;
        }
    }

    /**
     * Gets the users verified flag from item
     *
     * @return {boolean}
     */
    get verified() : boolean {
        const propertySet = metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( 'User', 'Verified' ),
              property    = propertySet && this.item ? this.item.properties.get( propertySet.propertyType.id ) : undefined;

        let retVal : boolean = propertySet?.valueDefault as boolean ?? false;

        if ( property?.value ) {
            retVal = Boolean( property.value );
        }

        return retVal;
    }

    /**
     * Sets the users verified flag to item
     *
     * @param {boolean} verified
     *
     * @return {void}
     */
    set verified( verified : boolean ) {
        const propertyType = metaModel[ 'CFG' ].getPropertyType( 'Verified' )

        if ( propertyType && this.item ) {
            let property = this.item.properties.get( propertyType.id );

            if ( !property ) {
                property = this.item.addProperty( propertyType.id );
            }

            property.value = verified;
        }
    }

    /**
     * Gets the users setupCompleted flag from item
     *
     * @return {boolean}
     */
    get setupCompleted() : boolean {
        const propertySet = metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( 'User', 'SetupCompleted' ),
              property    = propertySet && this.item ? this.item.properties.get( propertySet.propertyType.id ) : undefined;

        let retVal : boolean = propertySet?.valueDefault as boolean ?? false;

        if ( property?.value ) {
            retVal = Boolean( property.value );
        }

        return retVal;
    }

    /**
     * Sets the users setupCompleted flag to item
     *
     * @param {boolean} setupCompleted
     *
     * @return {void}
     */
    set setupCompleted( setupCompleted : boolean ) {
        const propertyType = metaModel[ 'CFG' ].getPropertyType( 'SetupCompleted' )

        if ( propertyType && this.item ) {
            let property = this.item.properties.get( propertyType.id );

            if ( !property ) {
                property = this.item.addProperty( propertyType.id );
            }

            property.value = setupCompleted;
        }
    }

    buildModuleString( module : string | string[] ) : string {
        if ( Array.isArray( module ) ) {
            return module.join( this.viewConfigSeparator );
        }
        else {
            return module;
        }
    }

    /**
     * Gets the views for module from item
     *
     * @param {string|string[]} module
     *
     * @return {View[]}
     */
    getViewsForModule( module : string | string[] ) : View[] {
        module = this.buildModuleString( module );

        const retVal : View[] = [],
              propertyType    = metaModel[ 'CFG' ].getPropertyType( 'ViewSelectorConfig' );

        if ( propertyType && this.item ) {
            const enumProperty   = this.item.enumProperties.get( propertyType.id ),
                  moduleSelector = new RegExp( `^${module}${this.viewConfigSeparator}` );

            if ( enumProperty ) {
                try {
                    for ( const [ key, property ] of enumProperty.properties ) {
                        const stringKey = convertValueToString( key, enumProperty.propertySet.keyType );

                        if ( moduleSelector.test( stringKey ) ) {
                            retVal.push( {
                                key       : stringKey,
                                label     : stringKey.replace( moduleSelector, '' ),
                                panelProps: JSON.parse( property.value as string )
                            } );
                        }
                    }
                }
                catch ( err ) {
                    error( err );
                }

                retVal.sort( ( val1, val2 ) => {
                    if ( val1.label > val2.label ) {
                        return 1;
                    }
                    else if ( val1.label < val2.label ) {
                        return -1;
                    }
                    else {
                        return 0;
                    }
                } );
            }
        }

        return retVal;
    }

    /**
     * Sets the views for module to item
     *
     * @param {string|string[]} module
     * @param {View[]} views
     *
     * @return {Promise<boolean>}
     */
    setViewsForModule( module : string | string[], views : View[] ) {
        module = this.buildModuleString( module );

        const propertyType              = metaModel[ 'CFG' ].getPropertyType( 'ViewSelectorConfig' ),
              moduleSelector            = new RegExp( `^${module}${this.viewConfigSeparator}` ),
              deletedStatusId           = metaModel[ 'CFG' ].getStatus( 'deleted' )!.id,
              processedKeys : string[ ] = [];

        if ( propertyType && this.item ) {
            let enumProperty = this.item.enumProperties.get( propertyType.id );

            //  if not exists add
            if ( !enumProperty ) {
                enumProperty = this.item.addEnumProperty( propertyType.id );
            }

            // add and update views
            for ( const view of views ) {
                const key   = `${module}${this.viewConfigSeparator}${view.label}`.trim(),
                      value = JSON.stringify( view.panelProps );

                let property = enumProperty.properties.get( key );

                if ( !property ) {
                    property = enumProperty.addProperty( key );
                }

                property.value = value;

                processedKeys.push( key );
            }

            // delete views
            for ( const [ key, property ] of enumProperty.properties ) {
                const stringKey = convertValueToString( key, enumProperty!.propertySet.keyType );

                if ( moduleSelector.test( stringKey ) && !processedKeys.includes( stringKey ) ) {
                    property.reset();
                    property.statusId = deletedStatusId;
                }
            }
        }
    }

    get itemContext() : string {
        return `user_${this.userId}`;
    }


    /*** rpc permission ********************************************************************************************************************/

    /**
     * Has the user the permission to call given RemoteProcedure.
     *
     * @param {string} module                   The api route module
     * @param {string} [command='default']      The api route command
     *
     * @return {boolean}
     */
    hasPermissionForRPC( module : string, command : string = 'default' ) : boolean {
        return this.rpcPermissions[ module ]?.[ command ] ?? false;
    }


    /*** group membership *****************************************************************************************************************/

    /**
     * Is the user member of at least one of the given groups.
     *
     * @param {string | string[]} group
     *
     * @return {boolean}
     */
    isMemberOfGroup( group : string | string[] ) : boolean {
        const groups : { [ groupName : string ] : true } = {};

        if ( !Array.isArray( group ) ) {
            groups[ group ] = true;
        }
        else {
            for ( const givenGroup of group ) {
                groups[ givenGroup ] = true;
            }
        }

        return this.groupMemberships.some( memberGroup => groups[ memberGroup ] );
    }


    /*** panelConfig ***********************************************************************************************************************/

    /**
     * Get users panelProps for given item type depending on users permission and preferGeneric
     *
     * @param {Schema} schema
     * @param {number | string} itemType
     * @param {PanelPropsWithPanelType['panelType'] | PanelPropsWithPanelType['panelType'] []} [excludePanelTypes=[]] panel types to be excluded, e.g. TreeView
     * @param {PanelPropsWithPanelType['panelType'] | PanelPropsWithPanelType['panelType'] []} [includePanelTypes=[]] panel types to be included, e.g. TreeView
     * @param {boolean} [preferGeneric=false]
     *
     * @return {PanelPropsWithPanelType[]}
     */
    getPanelPropsForItemType( schema : Schema, itemType : number | string, excludePanelTypes : PanelPropsWithPanelType['panelType'] | PanelPropsWithPanelType['panelType'][] = [], includePanelTypes : PanelPropsWithPanelType['panelType'] | PanelPropsWithPanelType['panelType'][] = [], preferGeneric : boolean = false ) : PanelPropsWithPanelType[] {
        let retVal : PanelPropsWithPanelType[] = [];

///////////////////////////////////
        const mmItemType           = metaModel[ schema ].getItemType( itemType ),
              itemTypeId           = mmItemType
                  ? mmItemType.id
                  : 0,
              hasGenericPermission = mmItemType
                  ? this.hasGenericPermissionOnItemType( 'read', mmItemType, schema )
                  : false,
              genericPanelProps    = hasGenericPermission
                  ? {
                      panelType: 'FinderDetail',
                      schema
                  }
                  : undefined;

        // convert to arrays
        excludePanelTypes = _.isArray( excludePanelTypes ) ? excludePanelTypes : [ excludePanelTypes ];
        includePanelTypes = _.isArray( includePanelTypes ) ? includePanelTypes : [ includePanelTypes ];

        // TODO: for now just the FinderDetail will be used if preferGeneric (command) was pressed. Implement a user defined decision for that.
        if (
            ( hasGenericPermission && preferGeneric )
            || !this.itemTypeToPanelConfigMapping[ schema ]?.[ itemTypeId ]?.length
        ) {
            if ( genericPanelProps ) {
                retVal.push( genericPanelProps as PanelPropsWithPanelType );
            }
        }
        else {
            const isMobile = matchMedia( 'screen and (max-width: 960px)' ).matches;

            retVal = this.itemTypeToPanelConfigMapping[ schema ]?.[ itemTypeId ]

                // get the panelConfigs
                .reduce(
                    ( innerRetVal, panelConfigId ) => {
                        const panelConfig = this.panelConfigs[ panelConfigId ];

                        if (
                            panelConfig?.panelConfig
                            && (
                                !excludePanelTypes.length
                                || !excludePanelTypes.includes( panelConfig.panelConfig.panelType )
                            )
                            && (
                                !includePanelTypes.length
                                || includePanelTypes.includes( panelConfig.panelConfig.panelType )
                            )
                        ) {
                            innerRetVal.push( panelConfig );
                        }

                        return innerRetVal;
                    },
                    [] as PanelConfig[]
                )

                // sort first by isMobile if and then by order
                ?.sort(
                    ( panelConfig1, panelConfig2 ) : -1 | 0 | 1 => {
                        if ( isMobile && panelConfig1.meta.isMobileOptimized && !panelConfig2.meta.isMobileOptimized ) {
                            return -1;
                        }
                        else if ( isMobile && !panelConfig1.meta.isMobileOptimized && panelConfig2.meta.isMobileOptimized ) {
                            return 1;
                        }
                        else if ( ( panelConfig1.meta.order ?? Number.POSITIVE_INFINITY ) > ( panelConfig2.meta.order ?? Number.POSITIVE_INFINITY ) ) {
                            return 1;
                        }
                        else if ( ( panelConfig2.meta.order ?? Number.POSITIVE_INFINITY ) > ( panelConfig1.meta.order ?? Number.POSITIVE_INFINITY ) ) {
                            return -1;
                        }
                        else {
                            return 0;
                        }
                    }
                )

                // extract only the panelConfig
                ?.map(
                    ( panelConfig ) => {
                        return { ...panelConfig.panelConfig };
                    }
                );
        }
//////////////////

        return retVal;
    }

    /**
     * Get users default panelProps for given item type depending on users permission and on option key
     *
     * @param {Schema} schema
     * @param {number | string} itemType
     * @param {boolean} optionKey
     * @param {'generic' | 'nongeneric'} [openAlwaysInMode]
     * @param {PanelPropsWithPanelType['panelType'] | PanelPropsWithPanelType['panelType'] []} [excludePanelTypes=[]] panel types to be excluded, e.g. TreeView
     * @param {PanelPropsWithPanelType['panelType'] | PanelPropsWithPanelType['panelType'] []} [includePanelTypes=[]] panel types to be included, e.g. TreeView
     *
     * @return {PanelPropsWithPanelType | undefined}
     */
    getDefaultPanelPropsForItemType( schema : Schema, itemType : number | string, optionKey : boolean = false, openAlwaysInMode? : 'generic' | 'nongeneric', excludePanelTypes : PanelPropsWithPanelType['panelType'] | PanelPropsWithPanelType['panelType'][] = [], includePanelTypes : PanelPropsWithPanelType['panelType'] | PanelPropsWithPanelType['panelType'][] = [] ) : PanelPropsWithPanelType | undefined {
        const mmItemType                                             = metaModel[ schema ].getItemType( itemType ),
              hasGenericPermission                                   = mmItemType
                  ? this.hasGenericPermissionOnItemType( 'read', mmItemType, schema )
                  : false,
              genericPanelProps : FinderDetailPanelProps | undefined = hasGenericPermission
                  ? {
                      panelType: 'FinderDetail',
                      schema
                  }
                  : undefined,
              panelConfig                                            = this.getPanelPropsForItemType( schema, itemType, excludePanelTypes, includePanelTypes )?.[ 0 ];

        if (
            (
                hasGenericPermission
                && (
                    openAlwaysInMode === 'generic'
                    || (
                        !openAlwaysInMode
                        && (
                            ( optionKey && this.viewToOpenByDefault === 'nongeneric' )
                            || ( !optionKey && this.viewToOpenByDefault === 'generic' )
                        )
                    )
                )
            )
            || !panelConfig
        ) {
            return genericPanelProps;
        }
        else {
            return panelConfig;
        }
    }

    /**
     * Returns the panelConfig by given panelConfigIds with the lowest order
     *
     * @param {number[]} panelConfigIds The item ids of the PanelConfigs
     * @param {PanelPropsWithPanelType['panelType'] | PanelPropsWithPanelType['panelType'] []} [excludePanelTypes=[]] panel types to be excluded, e.g. TreeView
     * @param {PanelPropsWithPanelType['panelType'] | PanelPropsWithPanelType['panelType'] []} [includePanelTypes=[]] panel types to be included, e.g. TreeView
     *
     * @return {PanelPropsWithPanelType | undefined}
     */
    getPanelConfigFromIdList( panelConfigIds : number[], excludePanelTypes : PanelPropsWithPanelType['panelType'] | PanelPropsWithPanelType['panelType'][] = [], includePanelTypes : PanelPropsWithPanelType['panelType'] | PanelPropsWithPanelType['panelType'][] = [] ) : PanelPropsWithPanelType | undefined {
        const isMobile = matchMedia( 'screen and (max-width: 960px)' ).matches;

        // convert to arrays
        excludePanelTypes = _.isArray( excludePanelTypes ) ? excludePanelTypes : [ excludePanelTypes ];
        includePanelTypes = _.isArray( includePanelTypes ) ? includePanelTypes : [ includePanelTypes ];

        return panelConfigIds

            // get the panelConfigs
            .reduce(
                ( innerRetVal, panelConfigId ) => {
                    const panelConfig = this.panelConfigs[ panelConfigId ];

                    if (
                        panelConfig?.panelConfig
                        && (
                            !excludePanelTypes.length
                            || !excludePanelTypes.includes( panelConfig.panelConfig.panelType )
                        )
                        && (
                            !includePanelTypes.length
                            || includePanelTypes.includes( panelConfig.panelConfig.panelType )
                        )
                    ) {
                        innerRetVal.push( panelConfig );
                    }

                    return innerRetVal;
                },
                [] as PanelConfig[]
            )

            // sort first by isMobile if and then by order
            ?.sort(
                ( panelConfig1, panelConfig2 ) : -1 | 0 | 1 => {
                    if ( isMobile && panelConfig1.meta.isMobileOptimized && !panelConfig2.meta.isMobileOptimized ) {
                        return -1;
                    }
                    else if ( isMobile && !panelConfig1.meta.isMobileOptimized && panelConfig2.meta.isMobileOptimized ) {
                        return 1;
                    }
                    else if ( ( panelConfig1.meta.order ?? Number.POSITIVE_INFINITY ) > ( panelConfig2.meta.order ?? Number.POSITIVE_INFINITY ) ) {
                        return 1;
                    }
                    else if ( ( panelConfig2.meta.order ?? Number.POSITIVE_INFINITY ) > ( panelConfig1.meta.order ?? Number.POSITIVE_INFINITY ) ) {
                        return -1;
                    }
                    else {
                        return 0;
                    }
                }
            )

            // extract only the panelConfig
            ?.map(
                ( panelConfig ) => {
                    return { ...panelConfig.panelConfig };
                }
            )

            // only the first one
            ?.[ 0 ];
    }

    /**
     * Checks if the current user has the permission to use the given panelConfig
     *
     * @param {number} panelConfigId The item id of the PanelConfig to check
     *
     * @return {PanelPropsWithPanelType | undefined}
     */
    getPanelConfig( panelConfigId : number ) : PanelPropsWithPanelType | undefined {
        return this.panelConfigs[ panelConfigId ] ? { ...this.panelConfigs[ panelConfigId ].panelConfig } : undefined;
    }


    /*** permission ***********************************************************************************************************************/

    /**
     * Returns the permission the current user has for the given ItemType
     *
     * @param {Number|String|ItemType} itemType                     The ItemTypeId, ItemTypeName or ItemType to check permission for.
     * @param {String} schema                                       The schema of the ItemType to check permission for.
     * @param {Boolean} throwError                                  Should an error thrown if the check fails.
     *                                                              Error numbers: 1011, 1012, 1013, 1014, 1015
     *
     * @return {String}                                             The permission.
     */
    getItemTypePermission( itemType : number | string | ItemType, schema : Schema = global.schema, throwError : boolean = false ) : YnPermissionName {
        let retVal : YnPermissionName = 'none',
            realItemType : ItemType | undefined,
            errorMessage              = '',
            errorNumber               = 0;

        if ( typeof itemType === 'string' || typeof itemType === 'number' ) {
            realItemType = metaModel[ schema ].getItemType( itemType );
        }
        else {
            realItemType = itemType;
        }

        const itemTypeId                 = realItemType?.id,
              itemTypeName               = realItemType?.name,
              methodErrorNumber : number = this.baseErrorNumber + 10;

        if ( !itemTypeId ) {
            errorMessage = 'No valid ItemType given to check permissions of the current user for!';
            errorNumber = methodErrorNumber + 1;
        }
        else if ( !this.permissions[ schema ] ) {
            errorMessage = 'No permissions for current user found!';
            errorNumber = methodErrorNumber + 2;
        }
        else if ( !this.permissions[ schema ]?.itemType ) {
            errorMessage = 'No ItemType permissions for current user found!';
            errorNumber = methodErrorNumber + 3;
        }
        else if ( !this.permissions[ schema ]?.itemType[ String( itemTypeId ) ] ) {
            errorMessage = `No permissions for ItemType ${itemTypeName} (${itemTypeId}) for current user found!`;
            errorNumber = methodErrorNumber + 4;
        }
        else if ( !this.permissions[ schema ]?.itemType[ String( itemTypeId ) ].permission ) {
            errorMessage = `No permission for ItemType ${itemTypeName} (${itemTypeId}) for current user found!`;
            errorNumber = methodErrorNumber + 5;
        }
        else {
            retVal = User.permissionNumberToNameMapping[ this.permissions[ schema ]?.itemType[ String( itemTypeId ) ].permission ?? 0 ];
        }

        if ( throwError && errorNumber > 0 ) {
            const checkError = new Error( 'Authorization Error' ) as ExtendedError;
            checkError.message = errorMessage;
            checkError.number = errorNumber;
            throw ( checkError );
        }

        return retVal;
    }

    /**
     * Returns the generic permission the current user has for the given ItemType
     *
     * @param {Number|String|ItemType} itemType                     The ItemTypeId, ItemTypeName or ItemType to check permission for.
     * @param {String} schema                                       The schema of the ItemType to check permission for.
     * @param {Boolean} throwError                                  Should an error thrown if the check fails.
     *                                                              Error numbers: 1021, 1022, 1023, 1024, 1025
     *
     * @return {String}                                             The generic permission.
     */
    getGenericItemTypePermission( itemType : number | string | ItemType, schema : Schema = global.schema, throwError : boolean = false ) : GenericPermissionName {
        let retVal : GenericPermissionName = 'none',
            realItemType : ItemType | undefined,
            errorMessage                   = '',
            errorNumber                    = 0;

        if ( typeof itemType === 'string' || typeof itemType === 'number' ) {
            realItemType = metaModel[ schema ].getItemType( itemType );
        }
        else {
            realItemType = itemType;
        }

        const itemTypeId                 = realItemType?.id,
              itemTypeName               = realItemType?.name,
              methodErrorNumber : number = this.baseErrorNumber + 10;

        if ( !itemTypeId ) {
            errorMessage = 'No valid ItemType given to check permissions of the current user for!';
            errorNumber = methodErrorNumber + 1;
        }
        else if ( !this.permissions[ schema ] ) {
            errorMessage = 'No permissions for current user found!';
            errorNumber = methodErrorNumber + 2;
        }
        else if ( !this.permissions[ schema ]?.itemType ) {
            errorMessage = 'No ItemType permissions for current user found!';
            errorNumber = methodErrorNumber + 3;
        }
        else if ( !this.permissions[ schema ]?.itemType[ String( itemTypeId ) ] ) {
            errorMessage = `No permissions for ItemType ${itemTypeName} (${itemTypeId}) for current user found!`;
            errorNumber = methodErrorNumber + 4;
        }
        else if ( !this.permissions[ schema ]?.itemType[ String( itemTypeId ) ].genericPermission ) {
            errorMessage = `No generic permission for ItemType ${itemTypeName} (${itemTypeId}) for current user found!`;
            errorNumber = methodErrorNumber + 5;
        }
        else {
            retVal = User.genericPermissionNumberToNameMapping[ this.permissions[ schema ]?.itemType[ String( itemTypeId ) ].genericPermission ?? 0 ];
        }

        if ( throwError && errorNumber > 0 ) {
            const checkError = new Error( 'Authorization Error' ) as ExtendedError;
            checkError.message = errorMessage;
            checkError.number = errorNumber;
            throw ( checkError );
        }

        return retVal;
    }

    /**
     * Checks if the current user has the given permission (and generic permission if) for the given ItemType
     *
     * @param { 'read', 'update', 'insert', 'delete' } permission   The permission to check for.
     * @param {Number|String|ItemType} itemType                     The ItemTypeId, ItemTypeName or ItemType to check permission for.
     * @param {String} schema                                       The schema of the ItemType to check permission for.
     * @param {Boolean} throwError                                  Should an error thrown if the check fails.
     *                                                              Error numbers: 1101
     *
     * @return {Boolean}                                            The check result.
     */
    hasPermissionOnItemType( permission : YnPermissionName, itemType : number | string | ItemType, schema : Schema = global.schema, throwError : boolean = false ) : boolean {
        let retVal : boolean = false;

        const checkPermission = this.getItemTypePermission( itemType, schema, throwError );

        if ( User.permissionNameToNumberMapping[ checkPermission ] < ( User.permissionNameToNumberMapping[ permission ] ?? 9 ) ) {
            if ( throwError ) {
                let realItemType : ItemType | undefined;
                if ( typeof itemType === 'string' || typeof itemType === 'number' ) {
                    realItemType = metaModel[ schema ].getItemType( itemType );
                }
                else {
                    realItemType = itemType;
                }

                const itemTypeId                 = realItemType?.id,
                      itemTypeName               = realItemType?.name,
                      methodErrorNumber : number = this.baseErrorNumber + 100,
                      checkError                 = new Error( 'Authorization Error' ) as ExtendedError;

                checkError.message = `Current user has no ${permission} permission for ItemType ${itemTypeName} (${itemTypeId})!`;
                checkError.number = methodErrorNumber + 1;
                throw ( checkError );
            }
        }
        else {
            retVal = true;
        }

        return retVal;
    }

    /**
     * Checks if the current user has the given generic permission for the given ItemType
     *
     * @param {GenericPermission} genericPermission                 The generic permission to check for.
     * @param {number|string|ItemType} itemType                     The ItemTypeId, ItemTypeName or ItemType to check permission for.
     * @param {Schema} schema                                       The schema of the ItemType to check permission for.
     * @param {Boolean} throwError                                  Should an error thrown if the check fails.
     *                                                              Error numbers: 1111
     *
     * @return {Boolean}                                            The check result.
     */
    hasGenericPermissionOnItemType( genericPermission : GenericPermissionName, itemType : number | string | ItemType, schema : Schema = global.schema, throwError : boolean = false ) : boolean {
        let retVal : boolean = false;

        const checkPermission = this.getGenericItemTypePermission( itemType, schema, throwError );

        if ( User.genericPermissionNameToNumberMapping[ checkPermission ] < ( User.genericPermissionNameToNumberMapping[ genericPermission ] ?? 9 ) ) {
            if ( throwError ) {
                let realItemType : ItemType | undefined;

                if ( typeof itemType === 'string' || typeof itemType === 'number' ) {
                    realItemType = metaModel[ schema ].getItemType( itemType );
                }
                else {
                    realItemType = itemType;
                }

                const itemTypeId                 = realItemType?.id,
                      itemTypeName               = realItemType?.name,
                      methodErrorNumber : number = this.baseErrorNumber + 110,
                      checkError                 = new Error( 'Authorization Error' ) as ExtendedError;

                checkError.message = `Current user has no generic ${genericPermission} permission for ItemType ${itemTypeName} (${itemTypeId})!`;
                checkError.number = methodErrorNumber + 1;
                throw ( checkError );
            }
        }
        else {
            retVal = true;
        }

        return retVal;
    }

    /**
     * Checks if the current user has the permission to use the given panelConfig
     *
     * @param {number} panelConfigId The item id of the PanelConfig to check
     *
     * @return {Boolean}             The check result.
     */
    hasPermissionOnPanelConfig( panelConfigId : number ) : boolean {
        return !!this.panelConfigs[ panelConfigId ];
    }

    /**
     * Returns the permission the current user has for the given PropertySet
     *
     * @param {number|PropertySet} propertySet                      The PropertySetId or PropertySet to check permission for.
     * @param {Schema} schema                                       The schema of the ItemType to check permission for.
     * @param {boolean} throwError                                  Should an error thrown if the check fails.
     *                                                              Error numbers: 1021, 1022, 1023, 1024, 1025
     *
     * @return {string}                                             The permission.
     */
    getPropertySetPermission( propertySet : number | PropertySet, schema : Schema = global.schema, throwError : boolean = false ) : YnPermissionName {
        let retVal : YnPermissionName = 'none',
            propertySetId : string | undefined,
            errorMessage              = '',
            errorNumber               = 0;

        if ( typeof propertySet !== 'number' ) {
            propertySetId = String( propertySet.id );
        }
        else {
            propertySetId = String( propertySet );
        }

        const methodErrorNumber = this.baseErrorNumber + 20;

        if ( !propertySet ) {
            errorMessage = 'No valid PropertySet given to check permissions of the current user for!';
            errorNumber = methodErrorNumber + 1;
        }
        else if ( !this.permissions[ schema ] ) {
            errorMessage = 'No permissions for current user found!';
            errorNumber = methodErrorNumber + 2;
        }
        else if ( !this.permissions[ schema ]?.propertySet ) {
            errorMessage = 'No PropertySet permissions for current user found!';
            errorNumber = methodErrorNumber + 3;
        }
        else if ( !this.permissions[ schema ]?.propertySet[ propertySetId ] ) {
            errorMessage = `No permissions for PropertySet ${propertySetId} for current user found!`;
            errorNumber = methodErrorNumber + 4;
        }
        else if ( !this.permissions[ schema ]?.propertySet[ propertySetId ].permission ) {
            errorMessage = `No permission for PropertySet ${propertySetId} for current user found!`;
            errorNumber = methodErrorNumber + 5;
        }
        else {
            retVal = User.permissionNumberToNameMapping[ this.permissions[ schema ]?.propertySet[ propertySetId ].permission ?? 0 ];
        }

        if ( throwError && errorNumber > 0 ) {
            const checkError = new Error( 'Authorization Error' ) as ExtendedError;
            checkError.message = errorMessage;
            checkError.number = errorNumber;
            throw ( checkError );
        }

        return retVal;
    }

    /**
     * Returns the generic permission the current user has for the given PropertySet
     *
     * @param {number|PropertySet} propertySet                      The PropertySetId or PropertySet to check permission for.
     * @param {Schema} schema                                       The schema of the ItemType to check permission for.
     * @param {boolean} throwError                                  Should an error thrown if the check fails.
     *                                                              Error numbers: 1031, 1032, 1033, 1034, 1035
     *
     * @return {string}                                             The generic permission.
     */
    getGenericPropertySetPermission( propertySet : number | PropertySet, schema : Schema = global.schema, throwError : boolean = false ) : GenericPermissionName {
        let retVal : GenericPermissionName = 'none',
            propertySetId : string | undefined,
            errorMessage                   = '',
            errorNumber                    = 0;

        if ( typeof propertySet !== 'number' ) {
            propertySetId = String( propertySet.id );
        }
        else {
            propertySetId = String( propertySet );
        }

        const methodErrorNumber = this.baseErrorNumber + 30;

        if ( !propertySet ) {
            errorMessage = 'No valid PropertySet given to check permissions of the current user for!';
            errorNumber = methodErrorNumber + 1;
        }
        else if ( !this.permissions[ schema ] ) {
            errorMessage = 'No permissions for current user found!';
            errorNumber = methodErrorNumber + 2;
        }
        else if ( !this.permissions[ schema ]?.propertySet ) {
            errorMessage = 'No PropertySet permissions for current user found!';
            errorNumber = methodErrorNumber + 3;
        }
        else if ( !this.permissions[ schema ]?.propertySet[ propertySetId ] ) {
            errorMessage = `No permissions for PropertySet ${propertySetId} for current user found!`;
            errorNumber = methodErrorNumber + 4;
        }
        else if ( !this.permissions[ schema ]?.propertySet[ propertySetId ].genericPermission ) {
            errorMessage = `No generic permission for PropertySet ${propertySetId} for current user found!`;
            errorNumber = methodErrorNumber + 5;
        }
        else {
            retVal = User.genericPermissionNumberToNameMapping[ this.permissions[ schema ]?.propertySet[ propertySetId ].genericPermission ?? 0 ];
        }

        if ( throwError && errorNumber > 0 ) {
            const checkError = new Error( 'Authorization Error' ) as ExtendedError;
            checkError.message = errorMessage;
            checkError.number = errorNumber;
            throw ( checkError );
        }

        return retVal;
    }

    /**
     * Checks if the current user has the given permission (and generic permission if) for the given PropertySet
     *
     * @param {YnPermissionName} permission                         The permission to check for.
     * @param {number|PropertySet} propertySet                      The PropertySetId or PropertySet to check permission for.
     * @param {Schema} schema                                       The schema of the PropertySet to check permission for.
     * @param {boolean} throwError                                  Should an error thrown if the check fails.
     *                                                              Error numbers: 1201
     *
     * @return {boolean}                                            The check result.
     */
    hasPermissionOnPropertySet( permission : YnPermissionName, propertySet : number | PropertySet, schema : Schema = global.schema, throwError : boolean = false ) : boolean {
        let retVal : boolean = false;

        const checkPermission = this.getPropertySetPermission( propertySet, schema, throwError );

        if ( User.permissionNameToNumberMapping[ checkPermission ] < ( User.permissionNameToNumberMapping[ permission ] ?? 9 ) ) {
            if ( throwError ) {
                let propertySetId : string | undefined;
                if ( typeof propertySet !== 'number' ) {
                    propertySetId = String( propertySet.id );
                }
                else {
                    propertySetId = String( propertySet );
                }

                const methodErrorNumber = this.baseErrorNumber + 200,
                      checkError        = new Error( 'Authorization Error' ) as ExtendedError;

                checkError.message = `Current user has no ${permission} permission for PropertySet ${propertySetId}!`;
                checkError.number = methodErrorNumber + 1;
                throw ( checkError );
            }
        }
        else {
            retVal = true;
        }

        return retVal;
    }

    /**
     * Checks if the current user has the given generic permission for the given PropertySet
     *
     * @param {GenericPermissionName} genericPermission             The generic permission to check for.
     * @param {number|PropertySet} propertySet                      The PropertySetId or PropertySet to check permission for.
     * @param {Schema} schema                                       The schema of the PropertySet to check generic permission for.
     * @param {boolean} throwError                                  Should an error thrown if the check fails.
     *                                                              Error numbers: 1211
     *
     * @return {boolean}                                            The check result.
     */
    hasGenericPermissionOnPropertySet( genericPermission : GenericPermissionName, propertySet : number | PropertySet, schema : Schema = global.schema, throwError : boolean = false ) : boolean {
        let retVal : boolean = false;

        const checkPermission = this.getGenericPropertySetPermission( propertySet, schema, throwError );

        if ( User.genericPermissionNameToNumberMapping[ checkPermission ] < ( User.genericPermissionNameToNumberMapping[ genericPermission ] ?? 9 ) ) {
            if ( throwError ) {
                let propertySetId : string | undefined;
                if ( typeof propertySet !== 'number' ) {
                    propertySetId = String( propertySet.id );
                }
                else {
                    propertySetId = String( propertySet );
                }

                const methodErrorNumber = this.baseErrorNumber + 210,
                      checkError        = new Error( 'Authorization Error' ) as ExtendedError;

                checkError.message = `Current user has no generic ${genericPermission} permission for PropertySet ${propertySetId}!`;
                checkError.number = methodErrorNumber + 1;
                throw ( checkError );
            }
        }
        else {
            retVal = true;
        }

        return retVal;
    }

    /**
     * Returns the permission the current user has for the given ReferenceSet
     *
     * @param {number|ReferenceSet} referenceSet                    The ReferenceSetId or ReferenceSet to check permission for.
     * @param {Schema} schema                                       The schema of the ItemType to check permission for.
     * @param {boolean} throwError                                  Should an error thrown if the check fails.
     *                                                              Error numbers: 1041, 1042, 1043, 1044, 1045
     *
     * @return {YnPermissionName}                                             The permission.
     */
    getReferenceSetPermission( referenceSet : number | ReferenceSet, schema : Schema = global.schema, throwError : boolean = false ) : YnPermissionName {
        let retVal : YnPermissionName = 'none',
            referenceSetId : string | undefined,
            errorMessage              = '',
            errorNumber               = 0;

        if ( typeof referenceSet !== 'number' ) {
            referenceSetId = String( referenceSet.id );
        }
        else {
            referenceSetId = String( referenceSet );
        }

        const methodErrorNumber = this.baseErrorNumber + 40;

        if ( !referenceSetId ) {
            errorMessage = 'No valid ReferenceSet given to check permissions of the current user for!';
            errorNumber = methodErrorNumber + 1;
        }
        else if ( !this.permissions[ schema ] ) {
            errorMessage = 'No permissions for current user found!';
            errorNumber = methodErrorNumber + 2;
        }
        else if ( !this.permissions[ schema ]?.referenceSet ) {
            errorMessage = 'No ReferenceSet permissions for current user found!';
            errorNumber = methodErrorNumber + 3;
        }
        else if ( !this.permissions[ schema ]?.referenceSet[ referenceSetId ] ) {
            errorMessage = `No permissions for ReferenceSet ${referenceSetId} for current user found!`;
            errorNumber = methodErrorNumber + 4;
        }
        else if ( !this.permissions[ schema ]?.referenceSet[ referenceSetId ].permission ) {
            errorMessage = `No permission for ReferenceSet ${referenceSetId} for current user found!`;
            errorNumber = methodErrorNumber + 5;
        }
        else {
            retVal = User.permissionNumberToNameMapping[ this.permissions[ schema ]?.referenceSet[ referenceSetId ].permission ?? 0 ];
        }

        if ( throwError && errorNumber > 0 ) {
            const checkError = new Error( 'Authorization Error' ) as ExtendedError;
            checkError.message = errorMessage;
            checkError.number = errorNumber;
            throw ( checkError );
        }

        return retVal;
    }

    /**
     * Returns the generic permission the current user has for the given ReferenceSet
     *
     * @param {number|ReferenceSet} referenceSet                    The ReferenceSetId or ReferenceSet to check permission for.
     * @param {Schema} schema                                       The schema of the ItemType to check permission for.
     * @param {boolean} throwError                                  Should an error thrown if the check fails.
     *                                                              Error numbers: 1051, 1052, 1053, 1054, 1055
     *
     * @return {GenericPermissionName}                                             The generic permission.
     */
    getGenericReferenceSetPermission( referenceSet : number | ReferenceSet, schema : Schema = global.schema, throwError : boolean = false ) : GenericPermissionName {
        let retVal : GenericPermissionName = 'none',
            referenceSetId : string | undefined,
            errorMessage                   = '',
            errorNumber                    = 0;

        if ( typeof referenceSet !== 'number' ) {
            referenceSetId = String( referenceSet.id );
        }
        else {
            referenceSetId = String( referenceSet );
        }

        const methodErrorNumber = this.baseErrorNumber + 50;

        if ( !referenceSetId ) {
            errorMessage = 'No valid ReferenceSet given to check permissions of the current user for!';
            errorNumber = methodErrorNumber + 1;
        }
        else if ( !this.permissions[ schema ] ) {
            errorMessage = 'No permissions for current user found!';
            errorNumber = methodErrorNumber + 2;
        }
        else if ( !this.permissions[ schema ]?.referenceSet ) {
            errorMessage = 'No ReferenceSet permissions for current user found!';
            errorNumber = methodErrorNumber + 3;
        }
        else if ( !this.permissions[ schema ]?.referenceSet[ referenceSetId ] ) {
            errorMessage = `No permissions for ReferenceSet ${referenceSetId} for current user found!`;
            errorNumber = methodErrorNumber + 4;
        }
        else if ( !this.permissions[ schema ]?.referenceSet[ referenceSetId ].genericPermission ) {
            errorMessage = `No generic permission for ReferenceSet ${referenceSetId} for current user found!`;
            errorNumber = methodErrorNumber + 5;
        }
        else {
            retVal = User.genericPermissionNumberToNameMapping[ this.permissions[ schema ]?.referenceSet[ referenceSetId ].genericPermission ?? 0 ];
        }

        if ( throwError && errorNumber > 0 ) {
            const checkError = new Error( 'Authorization Error' ) as ExtendedError;
            checkError.message = errorMessage;
            checkError.number = errorNumber;
            throw ( checkError );
        }

        return retVal;
    }

    /**
     * Checks if the current user has the given permission (and generic permission if) for the given ReferenceSet
     *
     * @param {YnPermissionName} permission                         The permission to check for.
     * @param {number|ReferenceSet} referenceSet                    The ReferenceSetId or ReferenceSet to check permission for.
     * @param {Schema} schema                                       The schema of the ReferenceSet to check permission for.
     * @param {boolean} throwError                                  Should an error thrown if the check fails.
     *                                                              Error numbers: 1301, 1302
     *
     * @return {boolean}                                            The check result.
     */
    hasPermissionOnReferenceSet( permission : YnPermissionName, referenceSet : number | ReferenceSet, schema : Schema = global.schema, throwError : boolean = false ) : boolean {
        let retVal : boolean = false;

        const checkPermission = this.getReferenceSetPermission( referenceSet, schema, throwError );

        if ( User.permissionNameToNumberMapping[ checkPermission ] < ( User.permissionNameToNumberMapping[ permission ] ?? 9 ) ) {
            if ( throwError ) {
                let referenceSetId : string;
                if ( typeof referenceSet !== 'number' ) {
                    referenceSetId = String( referenceSet.id );
                }
                else {
                    referenceSetId = String( referenceSet );
                }

                const methodErrorNumber = this.baseErrorNumber + 300,
                      checkError        = new Error( 'Authorization Error' ) as ExtendedError;

                checkError.message = `Current user has no ${permission} permission for ReferenceSet ${referenceSetId}!`;
                checkError.number = methodErrorNumber + 1;
                throw ( checkError );
            }
        }
        else {
            retVal = true;
        }

        return retVal;
    }

    /**
     * Checks if the current user has the given generic permission for the given ReferenceSet
     *
     * @param {GenericPermissionName} genericPermission             The generic permission to check for.
     * @param {number|ReferenceSet} referenceSet                    The ReferenceSetId or ReferenceSet to check permission for.
     * @param {Schema} schema                                       The schema of the ReferenceSet to check generic permission for.
     * @param {boolean} throwError                                  Should an error thrown if the check fails.
     *                                                              Error numbers: 1311
     *
     * @return {boolean}                                            The check result.
     */
    hasGenericPermissionOnReferenceSet( genericPermission : GenericPermissionName, referenceSet : number | ReferenceSet, schema : Schema = global.schema, throwError : boolean = false ) : boolean {
        let retVal : boolean = false;

        const checkPermission = this.getGenericReferenceSetPermission( referenceSet, schema, throwError );

        if ( User.genericPermissionNameToNumberMapping[ checkPermission ] < ( User.genericPermissionNameToNumberMapping[ genericPermission ] ?? 9 ) ) {
            if ( throwError ) {
                let referenceSetId : string;
                if ( typeof referenceSet !== 'number' ) {
                    referenceSetId = String( referenceSet.id );
                }
                else {
                    referenceSetId = String( referenceSet );
                }

                const methodErrorNumber = this.baseErrorNumber + 310,
                      checkError        = new Error( 'Authorization Error' ) as ExtendedError;

                checkError.message = `Current user has no generic ${genericPermission} permission for ReferenceSet ${referenceSetId}!`;
                checkError.number = methodErrorNumber + 1;
                throw ( checkError );
            }
        }
        else {
            retVal = true;
        }

        return retVal;
    }

    /**
     * Returns the permission the current user has for the given ReferencePropertySet
     *
     * @param {number|ReferencePropertySet} referencePropertySet    The ReferencePropertySetId or ReferencePropertySet to check permission for.
     * @param {Schema} schema                                       The schema of the ItemType to check permission for.
     * @param {boolean} throwError                                  Should an error thrown if the check fails.
     *                                                              Error numbers: 1061, 1062, 1063, 1064, 1065
     *
     * @return {YnPermissionName}                                   The permission.
     */
    getReferencePropertySetPermission( referencePropertySet : number | ReferencePropertySet, schema : Schema = global.schema, throwError : boolean = false ) : YnPermissionName {
        let retVal : YnPermissionName = 'none',
            referencePropertySetId : string | undefined,
            errorMessage              = '',
            errorNumber               = 0;

        if ( typeof referencePropertySet !== 'number' ) {
            referencePropertySetId = String( referencePropertySet.id );
        }
        else {
            referencePropertySetId = String( referencePropertySet );
        }

        const methodErrorNumber = this.baseErrorNumber + 60;

        if ( !referencePropertySetId ) {
            errorMessage = 'No valid ReferencePropertySet given to check permissions of the current user for!';
            errorNumber = methodErrorNumber + 1;
        }
        else if ( !this.permissions[ schema ] ) {
            errorMessage = 'No permissions for current user found!';
            errorNumber = methodErrorNumber + 2;
        }
        else if ( !this.permissions[ schema ]?.referencePropertySet ) {
            errorMessage = 'No ReferencePropertySet permissions for current user found!';
            errorNumber = methodErrorNumber + 3;
        }
        else if ( !this.permissions[ schema ]?.referencePropertySet[ referencePropertySetId ] ) {
            errorMessage = `No permissions for ReferencePropertySet ${referencePropertySetId} for current user found!`;
            errorNumber = methodErrorNumber + 4;
        }
        else if ( !this.permissions[ schema ]?.referencePropertySet[ referencePropertySetId ].permission ) {
            errorMessage = `No permission for ReferencePropertySet ${referencePropertySetId} for current user found!`;
            errorNumber = methodErrorNumber + 5;
        }
        else {
            retVal = User.permissionNumberToNameMapping[ this.permissions[ schema ]?.referencePropertySet[ referencePropertySetId ].permission ?? 0 ];
        }

        if ( throwError && errorNumber > 0 ) {
            const checkError = new Error( 'Authorization Error' ) as ExtendedError;
            checkError.message = errorMessage;
            checkError.number = errorNumber;
            throw ( checkError );
        }

        return retVal;
    }

    /**
     * Returns the generic permission the current user has for the given ReferencePropertySet
     *
     * @param {number|ReferencePropertySet} referencePropertySet    The ReferencePropertySetId or ReferencePropertySet to check permission for.
     * @param {Schema} schema                                       The schema of the ItemType to check permission for.
     * @param {boolean} throwError                                  Should an error thrown if the check fails.
     *                                                              Error numbers: 1071, 1072, 1073, 1074, 1075
     *
     * @return {GenericPermissionName}                              The generic permission.
     */
    getGenericReferencePropertySetPermission( referencePropertySet : number | ReferencePropertySet, schema : Schema = global.schema, throwError : boolean = false ) : GenericPermissionName {
        let retVal : GenericPermissionName = 'none',
            referencePropertySetId : string | undefined,
            errorMessage                   = '',
            errorNumber                    = 0;

        if ( typeof referencePropertySet !== 'number' ) {
            referencePropertySetId = String( referencePropertySet.id );
        }
        else {
            referencePropertySetId = String( referencePropertySet );
        }

        const methodErrorNumber = this.baseErrorNumber + 70;

        if ( !referencePropertySetId ) {
            errorMessage = 'No valid ReferencePropertySet given to check permissions of the current user for!';
            errorNumber = methodErrorNumber + 1;
        }
        else if ( !this.permissions[ schema ] ) {
            errorMessage = 'No permissions for current user found!';
            errorNumber = methodErrorNumber + 2;
        }
        else if ( !this.permissions[ schema ]?.referencePropertySet ) {
            errorMessage = 'No ReferencePropertySet permissions for current user found!';
            errorNumber = methodErrorNumber + 3;
        }
        else if ( !this.permissions[ schema ]?.referencePropertySet[ referencePropertySetId ] ) {
            errorMessage = `No permissions for ReferencePropertySet ${referencePropertySetId} for current user found!`;
            errorNumber = methodErrorNumber + 4;
        }
        else if ( !this.permissions[ schema ]?.referencePropertySet[ referencePropertySetId ].genericPermission ) {
            errorMessage = `No generic permission for ReferencePropertySet ${referencePropertySetId} for current user found!`;
            errorNumber = methodErrorNumber + 5;
        }
        else {
            retVal = User.genericPermissionNumberToNameMapping[ this.permissions[ schema ]?.referencePropertySet[ referencePropertySetId ].genericPermission ?? 0 ];
        }

        if ( throwError && errorNumber > 0 ) {
            const checkError = new Error( 'Authorization Error' ) as ExtendedError;
            checkError.message = errorMessage;
            checkError.number = errorNumber;
            throw ( checkError );
        }

        return retVal;
    }

    /**
     * Checks if the current user has the given permission (and generic permission if) for the given ReferencePropertySet
     *
     * @param {YnPermissionName} permission                         The permission to check for.
     * @param {number|ReferencePropertySet} referencePropertySet    The ReferencePropertySetId or ReferencePropertySet to check permission for.
     * @param {Schema} schema                                       The schema of the ReferencePropertySet to check permission for.
     * @param {boolean} throwError                                  Should an error thrown if the check fails.
     *                                                              Error numbers: 1401
     *
     * @return {boolean}                                            The check result.
     */
    hasPermissionOnReferencePropertySet( permission : YnPermissionName, referencePropertySet : number | ReferencePropertySet, schema : Schema = global.schema, throwError : boolean = false ) : boolean {
        let retVal : boolean = false;

        const checkPermission = this.getReferencePropertySetPermission( referencePropertySet, schema, throwError );

        if ( User.permissionNameToNumberMapping[ checkPermission ] < ( User.permissionNameToNumberMapping[ permission ] ?? 9 ) ) {
            if ( throwError ) {
                let referencePropertySetId : string;
                if ( typeof referencePropertySet !== 'number' ) {
                    referencePropertySetId = String( referencePropertySet.id );
                }
                else {
                    referencePropertySetId = String( referencePropertySet );
                }

                const methodErrorNumber = this.baseErrorNumber + 400,
                      checkError        = new Error( 'Authorization Error' ) as ExtendedError;

                checkError.message = `Current user has no ${permission} permission for ReferencePropertySet ${referencePropertySetId}!`;
                checkError.number = methodErrorNumber + 1;
                throw ( checkError );
            }
        }
        else {
            retVal = true;
        }

        return retVal;
    }

    /**
     * Checks if the current user has the given generic permission for the given ReferencePropertySet
     *
     * @param {GenericPermissionName} genericPermission             The generic permission to check for.
     * @param {number|ReferencePropertySet} referencePropertySet    The ReferencePropertySetId or ReferencePropertySet to check permission for.
     * @param {Schema} schema                                       The schema of the ReferencePropertySet to check generic permission for.
     * @param {boolean} throwError                                  Should an error thrown if the check fails.
     *                                                              Error numbers: 1411
     *
     * @return {boolean}                                            The check result.
     */
    hasGenericPermissionOnReferencePropertySet( genericPermission : GenericPermissionName, referencePropertySet : number | ReferencePropertySet, schema : Schema = global.schema, throwError : boolean = false ) : boolean {
        let retVal : boolean = false;

        const checkPermission = this.getGenericReferencePropertySetPermission( referencePropertySet, schema, throwError );

        if ( User.genericPermissionNameToNumberMapping[ checkPermission ] < ( User.genericPermissionNameToNumberMapping[ genericPermission ] ?? 9 ) ) {
            if ( throwError ) {
                let referencePropertySetId : string;
                if ( typeof referencePropertySet !== 'number' ) {
                    referencePropertySetId = String( referencePropertySet.id );
                }
                else {
                    referencePropertySetId = String( referencePropertySet );
                }

                const methodErrorNumber = this.baseErrorNumber + 410,
                      checkError        = new Error( 'Authorization Error' ) as ExtendedError;

                checkError.message = `Current user has no generic ${genericPermission} permission for ReferencePropertySet ${referencePropertySetId}!`;
                checkError.number = methodErrorNumber + 1;
                throw ( checkError );
            }
        }
        else {
            retVal = true;
        }

        return retVal;
    }


    /*** frontend *************************************************************************************************************************/
///////////////////////////////

//////////////


    /*** backend **************************************************************************************************************************/

///////////////////////////////

///////
/////////////////////////////////////////
///////
//////////////////////////////////
///////////////////////////////////////

///////////////////////////////////////////

/////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////////////
/////////

/////////////
////////////////////////////////////////////
/////////
/////////////////////
////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////
/////////////
/////////
/////

///////
//////////////////////////////
///////
/////////////////////////////////////
//////////////////////////////////////////////

//////////////////////////////////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////
/////////////
///////////////////////////////////////////////
//////////////////////////////////////////////////////
/////////
/////////////////////
//////////////
/////////
/////

//////////////////////////////////////
///////////////////////////////////////////////

//////////////////////////////////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////
/////////////
////////////////////////////////////////////////
/////////
/////////////////////
//////////////
/////////
/////

//////////////


    /*** private common *******************************************************************************************************************/

    private createWaitForReady() {
        let resolveFnc : Function | undefined,
            rejectFnc  : Function | undefined;

        this.waitForReady = new Promise<boolean>( ( resolve, reject ) => {
            resolveFnc = resolve;
            rejectFnc  = reject;
        } );

        this.waitForReady.resolve = resolveFnc;
        this.waitForReady.reject  = rejectFnc;
    }

    private fromJSON( json : UserDataFromJSON ) {
        if ( json.item && json.item.id !== -1 ) {
            const item = new Item( json.item, this.itemContext, 'CFG' );
            this._userItemId = item.id;
        }

        this.groupMemberships = json.groupMemberships ?? [];
        this.panelConfigs = json.panelConfigs ?? {};
        this.itemTypeToPanelConfigMapping = json.itemTypeToPanelConfigMapping ?? {};
        this.permissions = json.permissions ?? {};
        this.rpcPermissions = json.rpcPermissions ?? {};

        if ( json.applications ) {
            this.applications = json.applications.map( appData => new Application( appData ) );
        }

        if ( json.mainMenu ) {
            this.mainMenu = json.mainMenu;
        }

        this.isReady = true;
    }

    private validateUserId( userId : string ) {
        const re = /^[A-Za-z0-9@.:;_+-]+$/;
        if ( !userId || !re.test( userId ) ) {
            throw new LocalizableError( 'YN.userName.invalid' );
        }
    }


    /*** private frontend *****************************************************************************************************************/
///////////////////////////////

//////////////


    /*** private backend ******************************************************************************************************************/

///////////////////////////////

///////
///////////////////////////////////////////////////
///////
//////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////

/////////////
///////////////////////////////////////////////
//////////////////////////////////
///////////////////////////////
//////////////////////////////
//////////////

////////////////////////////////////////////
/////////
/////////////////////
////////////////////////////////
////////////////////////////////////////////////////////
////////////////////////
/////////

///////////////////////////////////
/////////////////
////////////////////////////////////////////////////////////
///////////////////////////////////////////////////
//////////////////////////////////////////////////////
//////////////////////////////////////////////////////////
/////////////
/////////////////////////
////////////////////////////////////
////////////////////////////////////////////////////////////
////////////////////////////
/////////////

/////////////////
////////////////////////////////////////////////////
/////////////
/////////////////////////////////////
/////////////

/////////////////
////////////////////////////////////////////////////
/////////////
/////////////////////////////////////
/////////////

/////////////////
////////////////////////////////////////////////////
/////////////
/////////////////////////////////////
/////////////

/////////

////////////////////////
/////

///////
/////////////////////////////////////////
/////////////////////////////////////////////////////////////////////////////////////////////////////////////
///////
////////////////////////////////////////////////////////
//////////////////////////////////////////////////////

/////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////

////////////////////
/////////////
/////////////////////////////////////////////////////////////////////////////////////////////////////////
/////////
/////////////////////
/////////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////
/////////

///////////////////////////////
////////////////////////////////////////////
/////////
//////////////
///////////////////////////////////////////////////////////////////////////////////////////////////////////////
/////////
/////

///////
//////////////////////////////////////////////////////////////////
//////
//////////////////////////////
///////
////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////

//////////////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////
///////////////////////////////////
////////////////////////////////////////////////////////////
////////////////////
///////////////////////////////////
///////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////
//////////////////////////////////////////////////////////////////////
///////////////////////////////////
//////////////////////////////////////////////////////
///////////////////
//////////////////
//////////////////////////////////////////////

///////////////////////////////////

/////////////
////////////////////////////////////////////////////////////////

//////////////////////////////////////
/////////////////////////////////////////////////////////////////////
/////////////

/////////////////////////////////////////////////////////////////////
/////////
/////////////////////
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/////////

/////

///////
/////////////////////////////////////////////////////
//////
//////////////////////////////
//////
///////////////
///////
///////////////////////////////////////////
/////////////////////////////////////////////////////////////

//////////////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////////////

/////////////////
////////////////
////////////////////////

//////////////////////////////

/////////////////////////////////

///////////////////////////////
/////////////
///////////////////////////////////////////////////////////////////////////////
/////////////////////////
////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////
///////////////////////
///////////////////////////////////////////
///////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////
////////////////////////
////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////

/////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////

////////////////////////////////////////////////////
//////////////////////////////////////////////////
/////////////////////////////////////////////////
/////////////////////////////////////////////////
/////////////////////////////////////////////////
////////////////////////////////////////////////
//////////////////////
/////////////////

//////////////////////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////
//////////////////
/////////////
/////////
/////////////////////
//////////////////////////////////
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/////////

//////////////////////////////////
/////////////
///////////////////////////////////////////////////////////////////////////////////
/////////////////////////
////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////
///////////////////////
///////////////////////////////////////////
///////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////
////////////////////////
////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////

/////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////

////////////////////////////////////////////////////
//////////////////////////////////////////////////
/////////////////////////////////////////////////
/////////////////////////////////////////////////
/////////////////////////////////////////////////
////////////////////////////////////////////////
//////////////////////
/////////////////

////////////////////////////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////
//////////////////
/////////////
/////////
/////////////////////
//////////////////////////////////
/////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/////////

///////////////////////////////////
/////////////
////////////////////////////////////////////////////////////////////////////////////
/////////////////////////
////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////
///////////////////////
///////////////////////////////////////////
///////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////
////////////////////////
////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////

/////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////

////////////////////////////////////////////////////
//////////////////////////////////////////////////
/////////////////////////////////////////////////
/////////////////////////////////////////////////
/////////////////////////////////////////////////
////////////////////////////////////////////////
//////////////////////
/////////////////

//////////////////////////////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////
//////////////////
/////////////
/////////
/////////////////////
//////////////////////////////////
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/////////

///////////////////////////////////////////
/////////////
////////////////////////////////////////////////////////////////////////////////////////////
/////////////////////////
/////////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////////////
///////////////////////
///////////////////////////////////////////
///////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////
////////////////////////
////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////

/////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////

////////////////////////////////////////////////////
//////////////////////////////////////////////////
/////////////////////////////////////////////////
/////////////////////////////////////////////////
/////////////////////////////////////////////////
////////////////////////////////////////////////
//////////////////////
/////////////////

//////////////////////////////////////////////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////
//////////////////
/////////////
/////////
/////////////////////
//////////////////////////////////
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/////////

//////////////////////////////////////////
/////////////
//////////////////////////////////////////////////////////////////////////
/////////////////////////
//////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////
///////////////////////
////////////////////////
/////////////////////////////////
///////////////////////////////////////////////////////
////////////////////////////////////////////////////////
///////////////////////////////
///////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////
//////////////////////////////////////////////////////////////
///////////////////////////////////
///////////////////////////
////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////

/////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////
/////////////////////////////////////////////////
/////////////////

///////////////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////
//////////////////
/////////////
/////////
/////////////////////
//////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/////////
/////

///////
/////////////////////////////////////////////////////////
//////
//////////////////////////////
//////
///////////////
///////
//////////////////////////////////////////////
//////////////////////////////////////////////////////////////////

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////

/////////////////////////////////

/////////////
//////////////////////////////////////////////////////////////////
/////////////////////////////
////////////////////////////
/////////////////////////////////////////////
////////////////////
////////////////////////////////////////////////////////////////////////

////////////////////////////////
///////////////////////////////////////////
///////////////////////////////////////////////////////////////////

//////////////////////////////////////////////
////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////

///////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////
/////////////////////////

////////////////////////////////////////////////////////////////////////
/////////////////////
/////////////////
/////////////
/////////
/////////////////////
/////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/////////
/////

////////////////////////////////////////////
//////////////////////////////////////////////////////////////

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////

///////////////////////////////

/////////////
//////////////////////////////////////////////////////////////////
/////////////////////////////
////////////////////////////
/////////////////////////////////////////////
////////////////////
////////////////////////////////////////////////////////////////////////

////////////////////////////////
///////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////

////////////////////////////////////////

//////////////////////////////////////////////////
/////////////////
/////////////
//////////////////
////////////////////////////////////////////////////////////////
/////////////
/////////
/////////////////////
///////////////////////////////////////
///////////////////////////////////////////////
/////////////
/////////////////////////////////////////////////////////////////////////////////////////////////////////////
/////////
/////

///////
////////////////////////////////////////////////////
//////
//////////////////////////////
///////
////////////////////////////////////////////
/////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////

/////////////////////////////////////////////////////////////////////////////////////////////

///////////////////////////

/////////////
////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
//////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////
//////////////////////
/////////////////
////////////////////////////////////
///////////////////////////////////////
//////////////////
////////////////////
//////////////

///////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////////////////////
/////////////

/////////////////////////////////////////////////////////////////

/////////////////////////
////////////////////////
//////////////////////
////////////////////////////////////////////////////////////
////////////////////////////////////
//////////////////////////////
/////////////////////////////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////

///////////////////////////////////////
//////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////////////////
/////////////////////

////////////////////////////////////////////////////
//////////////////
//////////////////////////
//////////////////////
//////////////////////////
//////////////////////////////////////////
////////////////////
//////////////
/////////
/////////////////////
///////////////////////////////////////
///////////////////////////////////////////////
/////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/////////
/////

///////
////////////////////////////////////
///////
////////////////////////////////////////////
//////////////////////////////////////////////////////////

///////////////////////////////////////////////////////////////

///////////////////////////////
///////////////////////////////////////////////

/////////////
/////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////
//////////////////////
/////////////////
///////////////////////////////////////
//////////////////
////////////////////
//////////////

///////////////////////////////////
////////////////////////////////////////////////////////////////
/////////////

/////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////////////////////////////////////////
//////////////////////////////
/////////////////////////////////////////////////////////////
//////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////
///////////////////////////////
///////////////////////////////////////////
////////////////////////////
//////////////////////////////////////
////////////////////////
/////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

///////////////////////////////
////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////////////////

////////////////////////////////////
/////////////////////////////////////////////////////////////////////////////////////

////////////////////////////////////////////////////
///////////////////////////////
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////////
//////////////////////////
///////////////////////////////////
//////////////////////
/////////////////
/////////////

//////////////////////////////////////////////////
///////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////////////////////////////////////////////

///////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////////////////////////////////////
/////////////////////
/////////////////
/////////////
/////////
/////////////////////
///////////////////////////////////////
///////////////////////////////////////////////
/////////////
//////////////////////////////////////////////////////////////////////////////////////////////////////////////
/////////
/////

/////////////////////////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////////////////////////

//////////////////////////////

////////////////////////////
//////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////
///////////////////////////////
//////////////
/////////

//////////////
/////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

//////////////////////////////////////////

///////////////////////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/////////////////
//////////////////////
//////////////////////////////////////////////////////////////////////////////////////////
/////////////////
/////////////
//////////////////
/////////////////////////////////////////////////////////////////////////////////////
/////////////

/////////////////////////////
//////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////
//////////////////////
/////////////////////
////////////////////////////
////////////////////////////
//////////////////////////////////
/////////////////
//////////////
/////////

////////////////////////////////////////////
//////////////////////////////////////////
/////////
//////////////
//////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////
/////////

//////////////////////
/////

///////
/////////////////////////////////////////
//////////////////////////////////////////////////////////////////////
///////////////
///////
//////////////////////////////////////////////////

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
///////////////////////////////////
//////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////
/////////////////////////////////////////////////////////
/////////////////
//////////////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
//////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/////////////////////////
/////////////////////
///////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////
/////////////////////////////
/////////////////////////
/////////////////////
/////////////////
/////////////
/////////
/////

//////////////
}

export default User;
